import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Spinner,
  Alert,
  Button,
  Form,
  InputGroup,
  Row,
  Col,
  Modal,
  Badge,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStream,
  faEye,
  faSearch,
  faTimes,
  faFilter,
  faMobileAlt,
} from '@fortawesome/free-solid-svg-icons';
import Pagination from '../common/Pagination';
import RawJsonViewer from '../common/RawJsonViewer';
import useDebounce from '../../hooks/useDebounce';
import DeviceTraceModal from './DeviceTraceModal';
import {
  formatDateTime,
  methodVariant,
  statusVariant,
  normalizeJson,
  shortAction,
} from './activityLogUtils';
import {
  fetchMobileActivityLogs,
  fetchMobileActivityLogById,
  fetchMobileActivityFilters,
} from '../../services/mobileActivityLogApi';

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 500;

const MobileActivityLogs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [filterOptions, setFilterOptions] = useState({
    route_uris: [],
    methods: [],
    platforms: [],
    app_versions: [],
    status_codes: [],
    retention_days: null,
  });

  const [deviceId, setDeviceId] = useState('');
  const [email, setEmail] = useState('');
  const [routeUri, setRouteUri] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('all'); // 'all' | 'success' | 'failed'
  const [statusCode, setStatusCode] = useState('');
  const [platform, setPlatform] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const debouncedDeviceId = useDebounce(deviceId, SEARCH_DEBOUNCE_MS);
  const debouncedEmail = useDebounce(email, SEARCH_DEBOUNCE_MS);
  const effectiveSearch = debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : '';
  const effectiveDeviceId = debouncedDeviceId.trim();
  const effectiveEmail = debouncedEmail.trim();

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailLog, setDetailLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [traceDeviceId, setTraceDeviceId] = useState(null);
  const [showTrace, setShowTrace] = useState(false);

  const activeFilters = {
    ...(effectiveDeviceId ? { device_id: effectiveDeviceId } : {}),
    ...(effectiveEmail ? { email: effectiveEmail } : {}),
    ...(routeUri ? { route_uri: routeUri } : {}),
    ...(method ? { method } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...(statusCode ? { status_code: statusCode } : {}),
    ...(platform ? { platform } : {}),
    ...(appVersion ? { app_version: appVersion } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
    ...(effectiveSearch ? { q: effectiveSearch } : {}),
  };
  const activeFiltersKey = JSON.stringify(activeFilters);

  useEffect(() => {
    let cancelled = false;
    fetchMobileActivityFilters()
      .then((res) => {
        if (cancelled || !res.data?.success || !res.data?.data) return;
        setFilterOptions((prev) => ({ ...prev, ...res.data.data }));
      })
      .catch(() => {
        // Dropdowns fall back to free-text filters; not worth an error banner.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFiltersKey]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchMobileActivityLogs({
          ...activeFilters,
          page: currentPage,
          items_per_page: PER_PAGE,
        });
        if (cancelled) return;
        const payload = res.data;
        if (payload?.success && payload?.data) {
          setItems(Array.isArray(payload.data.items) ? payload.data.items : []);
          setTotalItems(payload.data.objects?.total ?? 0);
        } else {
          setItems([]);
          setTotalItems(0);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to fetch mobile activity logs');
        setItems([]);
        setTotalItems(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeFiltersKey]);

  const hasActiveFilters = Object.keys(activeFilters).length > 0 || searchTerm.trim();

  const clearFilters = () => {
    setDeviceId('');
    setEmail('');
    setRouteUri('');
    setMethod('');
    setStatus('all');
    setStatusCode('');
    setPlatform('');
    setAppVersion('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const openDetail = async (id) => {
    setShowDetail(true);
    setDetailLog(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await fetchMobileActivityLogById(id);
      if (res.data?.success && res.data?.data) {
        setDetailLog(res.data.data);
      } else {
        setDetailError(res.data?.message || 'Log not found');
      }
    } catch (err) {
      setDetailError(err.response?.data?.message || err.message || 'Failed to load activity log');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setShowDetail(false);
    setDetailLog(null);
    setDetailError(null);
  };

  const openTrace = (id) => {
    if (!id) return;
    setTraceDeviceId(id);
    setShowTrace(true);
  };

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FontAwesomeIcon icon={faStream} className="me-2" />
          Mobile Activity Logs
        </h2>
      </div>
      <p className="text-muted mb-3">
        Every mobile API call (<code>/api/*</code> and <code>/songs/*</code>), with request payload and outcome.
        {filterOptions.retention_days
          ? ` Retained for ${filterOptions.retention_days} days.`
          : ''}{' '}
        Use <strong>Trace</strong> on a row to replay everything one device did.
      </p>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Form.Group className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search device / email / path / payload / error (min 3 characters)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
            </InputGroup>
          </Form.Group>

          <Row className="g-2 align-items-end">
            <Col xs={12} md={3}>
              <Form.Label className="small mb-1">
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Device ID
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="device_id"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small mb-1">Email</Form.Label>
              <Form.Control
                type="text"
                placeholder="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small mb-1">Endpoint</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={routeUri}
                onChange={(e) => setRouteUri(e.target.value)}
              >
                <option value="">All endpoints</option>
                {(filterOptions.route_uris || []).map((uri) => (
                  <option key={uri} value={uri}>
                    {uri}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Method</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                <option value="">All</option>
                {(filterOptions.methods || []).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Result</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Status code</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={statusCode}
                onChange={(e) => setStatusCode(e.target.value)}
              >
                <option value="">All</option>
                {(filterOptions.status_codes || []).map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Platform</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
              >
                <option value="">All</option>
                {(filterOptions.platforms || []).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">App version</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={appVersion}
                onChange={(e) => setAppVersion(e.target.value)}
              >
                <option value="">All</option>
                {(filterOptions.app_versions || []).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">From</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">To</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Col>
            {hasActiveFilters && (
              <Col xs="auto">
                <Button variant="outline-secondary" onClick={clearFilters}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Clear filters
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={PER_PAGE}
            onPageChange={setCurrentPage}
            position="top"
          />

          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="table-vcenter mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Time</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th className="text-end">ms</th>
                    <th>Device</th>
                    <th>User</th>
                    <th>Client</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No mobile activity logs found
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td className="text-nowrap">{formatDateTime(row.created_at)}</td>
                        <td>
                          <Badge bg={methodVariant(row.method)} className="text-white me-1">
                            {row.method}
                          </Badge>
                          <code className="small">{row.path}</code>
                          {row.error_message && (
                            <div className="text-danger small text-truncate" style={{ maxWidth: '320px' }}>
                              {row.error_message}
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge bg={statusVariant(row.status_code)} className="text-white">
                            {row.status_code}
                          </Badge>
                        </td>
                        <td className="text-end">{row.duration_ms}</td>
                        <td>
                          <code className="small">{row.device_id || '—'}</code>
                        </td>
                        <td>
                          <div className="small">{row.email || '—'}</div>
                          <div className="text-muted small">
                            {row.user_id ? `ID: ${row.user_id}` : 'anonymous'}
                          </div>
                        </td>
                        <td className="small">
                          <div>{row.platform || '—'} {row.app_version || ''}</div>
                          <div className="text-muted">{row.device_model || ''}</div>
                        </td>
                        <td className="text-end text-nowrap">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            onClick={() => openDetail(row.id)}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={!row.device_id}
                            onClick={() => openTrace(row.device_id)}
                          >
                            <FontAwesomeIcon icon={faMobileAlt} className="me-1" />
                            Trace
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={PER_PAGE}
            onPageChange={setCurrentPage}
            position="bottom"
          />
        </Card.Body>
      </Card>

      <Modal show={showDetail} onHide={closeDetail} size="xl" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>
            Activity
            {detailLog?.id ? ` #${detailLog.id}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" className="mb-3" />
              <p className="mb-0">Loading activity...</p>
            </div>
          ) : detailError ? (
            <Alert variant="danger" className="mb-0">
              {detailError}
            </Alert>
          ) : detailLog ? (
            <>
              <Row className="mb-3 g-2">
                <Col md={4}>
                  <div className="text-muted small">Endpoint</div>
                  <div className="text-break">
                    <Badge bg={methodVariant(detailLog.method)} className="text-white me-1">
                      {detailLog.method}
                    </Badge>
                    <code>{detailLog.path}</code>
                  </div>
                  {/* The route pattern only adds information when it differs
                      from the concrete path (i.e. it has parameters). */}
                  {detailLog.route_uri && detailLog.route_uri !== detailLog.path && (
                    <div className="text-muted small text-break">{detailLog.route_uri}</div>
                  )}
                </Col>
                <Col md={2}>
                  <div className="text-muted small">Status</div>
                  <div>
                    <Badge bg={statusVariant(detailLog.status_code)} className="text-white">
                      {detailLog.status_code}
                    </Badge>{' '}
                    <span className="text-muted small">{detailLog.duration_ms} ms</span>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Device</div>
                  <div className="text-break">
                    <code>{detailLog.device_id || '—'}</code>
                  </div>
                  {detailLog.device_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0"
                      onClick={() => {
                        closeDetail();
                        openTrace(detailLog.device_id);
                      }}
                    >
                      Trace this device
                    </Button>
                  )}
                </Col>
                <Col md={3}>
                  <div className="text-muted small">User</div>
                  <div className="text-break">{detailLog.email || '—'}</div>
                  <div className="text-muted small">
                    {detailLog.user_id ? `ID: ${detailLog.user_id}` : 'anonymous'}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3 g-2">
                <Col md={3}>
                  <div className="text-muted small">Created</div>
                  <div>{formatDateTime(detailLog.created_at)}</div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Client</div>
                  <div className="text-break">
                    {detailLog.platform || '—'} {detailLog.app_version || ''}
                  </div>
                  <div className="text-muted small text-break">
                    {detailLog.os || ''} {detailLog.os_version || ''} {detailLog.device_model || ''}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">IP / Country</div>
                  <div>{detailLog.ip || '—'}</div>
                  <div className="text-muted small">{detailLog.country_code || ''}</div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Route action</div>
                  <div className="small text-break" title={detailLog.action || ''}>
                    {shortAction(detailLog.action)}
                  </div>
                </Col>
              </Row>

              {detailLog.error_message && (
                <Alert variant="danger" className="mb-3">
                  <div className="fw-bold">Error</div>
                  <div className="small text-break">{detailLog.error_message}</div>
                </Alert>
              )}

              <h6 className="mt-3">Request body</h6>
              {normalizeJson(detailLog.request_body) ? (
                <RawJsonViewer data={normalizeJson(detailLog.request_body)} maxHeight="35vh" />
              ) : (
                <div className="bg-light p-3 rounded border text-muted mb-3">Empty body</div>
              )}

              <h6 className="mt-3">Query string</h6>
              {normalizeJson(detailLog.query) ? (
                <RawJsonViewer data={normalizeJson(detailLog.query)} maxHeight="25vh" />
              ) : (
                <div className="bg-light p-3 rounded border text-muted mb-3">No query params</div>
              )}

              <h6 className="mt-3">Response</h6>
              {normalizeJson(detailLog.response_summary) ? (
                <RawJsonViewer data={normalizeJson(detailLog.response_summary)} maxHeight="30vh" />
              ) : (
                <div className="bg-light p-3 rounded border text-muted">No response captured</div>
              )}
            </>
          ) : (
            <div className="bg-light p-3 rounded border text-muted">No activity available</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetail}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <DeviceTraceModal
        show={showTrace}
        deviceId={traceDeviceId}
        filters={activeFilters}
        onClose={() => setShowTrace(false)}
        onViewLog={(id) => {
          setShowTrace(false);
          openDetail(id);
        }}
      />
    </div>
  );
};

export default MobileActivityLogs;
